import api from 'api';
import classNames from 'classnames';
import BareHeading from 'components/Headings/BareHeading';
import Table from 'components/Tables/Table';
import TableHeader from 'components/Tables/Table/components/TableHeader';
import { diff } from 'deep-object-diff';
import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import ProtectedComponent from 'router/components/ProtectedComponent';
import RevertPortalSettingsButton from 'scenes/Administration/PortalManagementPage/components/RevertPortalSettingsButton';
import SavePortalSettingsButton from 'scenes/Administration/PortalManagementPage/components/SavePortalSettingsButton';
import toastr from 'toastr';
import authEnums from 'utilities/enums/authEnums';
import TableHeaders from 'utilities/services/TableHeaders';
import NotificationsSettingsTableRow from './components/NotificationSettingsTableRow';
import './styles.scss';

const NotificationSettings = ({ className }) => {
  const classes = classNames('ickyc-page', 'ickyc-notification-settings', className);
  const [settings, setSettings] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = useCallback(
    async (values, form) => {
      try {
        setIsLoading(true);
        const initialValues = { ...form.getState().initialValues };
        const { generalNotificationSettings, adminNotificationSettings } = diff(initialValues, values);
        let alteredNotificationSettings = [];

        const mapNotificationSettings = (newSettings, initialSettings) => {
          return Object.entries(newSettings).map(([key, value]) => ({
            notificationType: initialSettings[key].notificationType,
            askToApprove: initialSettings[key].askToApprove,
            dashboardNotification: initialSettings[key].dashboardNotification,
            emailNotification: initialSettings[key].emailNotification,
            ...value,
          }));
        };

        if (generalNotificationSettings) {
          alteredNotificationSettings = [
            ...alteredNotificationSettings,
            ...mapNotificationSettings(generalNotificationSettings, initialValues?.generalNotificationSettings),
          ];
        }
        if (adminNotificationSettings) {
          alteredNotificationSettings = [
            ...alteredNotificationSettings,
            ...mapNotificationSettings(adminNotificationSettings, initialValues?.adminNotificationSettings),
          ];
        }

        await api.notifications.updateNotificationsSettings(alteredNotificationSettings);
        toastr.success('Successfully updated Notification Settings');
        setSettings(values);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [diff],
  );

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        setIsLoading(true);
        const { data } = await api.notifications.fetchNotificationsSettings();
        setSettings(data);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotificationSettings();
  }, []);

  return (
    <div className={classes}>
      <Form onSubmit={onSubmit} initialValues={settings}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <BareHeading title="Notification Settings">
              <RevertPortalSettingsButton />
              <SavePortalSettingsButton />
            </BareHeading>

            <Table
              values={settings?.generalNotificationSettings}
              tableRow={NotificationsSettingsTableRow}
              updating={isLoading}
              headerRow={TableHeader}
              headerData={{
                items: TableHeaders.NotificationsSettingsTableHeader('General Notifications'),
              }}
              namePreffix="generalNotificationSettings"
            />
            <ProtectedComponent
              allowedUserTypes={[authEnums.USER_TYPES.FULL_ADMIN, authEnums.USER_TYPES.RESTRICTED_ADMIN]}
            >
              <Table
                values={settings?.adminNotificationSettings}
                tableRow={NotificationsSettingsTableRow}
                updating={isLoading}
                headerRow={TableHeader}
                headerData={{
                  items: TableHeaders.NotificationsSettingsTableHeader('Admin Notifications'),
                }}
                namePreffix="adminNotificationSettings"
              />
            </ProtectedComponent>
          </form>
        )}
      </Form>
    </div>
  );
};
export default NotificationSettings;
